/**
 * Contador Programable para Shopify
 * Versión: 1.0.0
 * 
 * Maneja contadores programables con control de fechas y horarios
 */
class ProgrammableCountdown {
  constructor(element) {
    this.element = element;
    this.config = this.parseConfig();
    this.intervals = [];
    this.isVisible = false;
    
    this.init();
  }

  /**
   * Parsea la configuración desde los data attributes
   */
  parseConfig() {
    const el = this.element;
    
    return {
      id: el.id,
      startDate: el.dataset.startDate,
      endDate: el.dataset.endDate,
      startTime: el.dataset.startTime,
      endTime: el.dataset.endTime,
      showAllDay: el.dataset.showAllDay === 'true',
      timezone: el.dataset.timezone || 'America/New_York',
      showDays: el.dataset.showDays === 'true',
      showHours: el.dataset.showHours === 'true',
      showMinutes: el.dataset.showMinutes === 'true',
      showSeconds: el.dataset.showSeconds === 'true'
    };
  }

  /**
   * Inicializa el contador
   */
  init() {
    // Verificar si debe mostrarse inicialmente
    this.checkVisibility();
    
    // Configurar intervalos de verificación
    this.setupIntervals();
    
    // Si está visible, iniciar el countdown
    if (this.isVisible) {
      this.startCountdown();
    }
  }

  /**
   * Verifica si el contador debe ser visible según fecha y hora
   */
  checkVisibility() {
    const now = new Date();
    const currentDate = this.formatDate(now);
    const currentTime = this.formatTime(now);
    
    // Verificar rango de fechas
    const isInDateRange = this.isDateInRange(currentDate);
    
    // Verificar rango de horarios (solo si no es "todo el día")
    const isInTimeRange = this.config.showAllDay || this.isTimeInRange(currentTime);
    
    // Calcular fecha objetivo automáticamente
    const targetDateTime = this.calculateTargetDate();
    const hasNotExpired = now < targetDateTime;
    
    const shouldBeVisible = isInDateRange && isInTimeRange && hasNotExpired;
    
    if (shouldBeVisible !== this.isVisible) {
      this.toggleVisibility(shouldBeVisible);
    }
  }

  /**
   * Calcula la fecha objetivo automáticamente basada en la fecha de fin
   */
  calculateTargetDate() {
    if (this.config.showAllDay) {
      // Si es todo el día, contar hasta el final del último día
      return new Date(this.config.endDate + ' 23:59:59');
    } else {
      // Si es franja horaria, contar hasta la hora de fin del último día
      return new Date(this.config.endDate + ' ' + this.config.endTime + ':59');
    }
  }

  /**
   * Verifica si una fecha está en el rango configurado
   */
  isDateInRange(dateStr) {
    const date = new Date(dateStr);
    const startDate = new Date(this.config.startDate);
    const endDate = new Date(this.config.endDate);
    
    // Ajustar las fechas para comparar solo el día (sin hora)
    date.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    return date >= startDate && date <= endDate;
  }

  /**
   * Verifica si una hora está en el rango configurado
   */
  isTimeInRange(timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const currentMinutes = hours * 60 + minutes;
    
    const [startHours, startMins] = this.config.startTime.split(':').map(Number);
    const startMinutes = startHours * 60 + startMins;
    
    const [endHours, endMins] = this.config.endTime.split(':').map(Number);
    const endMinutes = endHours * 60 + endMins;
    
    // Manejar casos donde el rango cruza medianoche
    if (startMinutes <= endMinutes) {
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    } else {
      return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
    }
  }

  /**
   * Alterna la visibilidad del contador
   */
  toggleVisibility(visible) {
    this.isVisible = visible;
    
    if (visible) {
      this.element.style.display = 'block';
      this.startCountdown();
      this.fireEvent('countdown:show');
    } else {
      this.element.style.display = 'none';
      this.stopCountdown();
      this.fireEvent('countdown:hide');
    }
  }

  /**
   * Configura los intervalos de verificación
   */
  setupIntervals() {
    // Verificar visibilidad cada minuto
    const visibilityInterval = setInterval(() => {
      this.checkVisibility();
    }, 60000);
    
    this.intervals.push(visibilityInterval);
  }

  /**
   * Inicia el countdown
   */
  startCountdown() {
    // Limpiar countdown existente
    this.stopCountdown();
    
    // Actualizar inmediatamente
    this.updateCountdown();
    
    // Configurar actualización cada segundo
    const countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
    
    this.intervals.push(countdownInterval);
  }

  /**
   * Detiene el countdown
   */
  stopCountdown() {
    this.intervals.forEach(interval => clearInterval(interval));
    this.intervals = [];
  }

  /**
   * Actualiza la visualización del countdown
   */
  updateCountdown() {
    const now = new Date();
    const target = this.calculateTargetDate();
    const diff = target - now;
    
    if (diff <= 0) {
      this.handleExpired();
      return;
    }
    
    const timeLeft = this.calculateTimeLeft(diff);
    this.updateDisplay(timeLeft);
  }

  /**
   * Calcula el tiempo restante
   */
  calculateTimeLeft(milliseconds) {
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds };
  }

  /**
   * Actualiza la visualización con los valores calculados
   */
  updateDisplay(timeLeft) {
    const { days, hours, minutes, seconds } = timeLeft;
    
    // Solo actualizar elementos que existen en el DOM (no filtrados por Liquid)
    const daysEl = this.element.querySelector('[data-days]');
    if (daysEl && this.config.showDays) {
      daysEl.textContent = this.padZero(days);
    }
    
    const hoursEl = this.element.querySelector('[data-hours]');
    if (hoursEl && this.config.showHours) {
      hoursEl.textContent = this.padZero(hours);
    }
    
    const minutesEl = this.element.querySelector('[data-minutes]');
    if (minutesEl && this.config.showMinutes) {
      minutesEl.textContent = this.padZero(minutes);
    }
    
    const secondsEl = this.element.querySelector('[data-seconds]');
    if (secondsEl && this.config.showSeconds) {
      secondsEl.textContent = this.padZero(seconds);
    }
  }

  /**
   * Maneja cuando el countdown expira
   */
  handleExpired() {
    this.stopCountdown();
    
    // Ocultar el countdown y mostrar mensaje de expirado
    const countdownDisplay = this.element.querySelector('.countdown-display');
    const expiredMessage = this.element.querySelector('.countdown-expired');
    
    if (countdownDisplay) countdownDisplay.style.display = 'none';
    if (expiredMessage) expiredMessage.style.display = 'block';
    
    this.fireEvent('countdown:expired');
  }

  /**
   * Dispara eventos personalizados
   */
  fireEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail: { countdown: this, config: this.config, ...detail },
      bubbles: true
    });
    
    this.element.dispatchEvent(event);
  }

  /**
   * Utilidades de formato
   */
  padZero(num) {
    return num.toString().padStart(2, '0');
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = this.padZero(date.getMonth() + 1);
    const day = this.padZero(date.getDate());
    return `${year}-${month}-${day}`;
  }

  formatTime(date) {
    const hours = this.padZero(date.getHours());
    const minutes = this.padZero(date.getMinutes());
    return `${hours}:${minutes}`;
  }

  /**
   * Destruye el contador y limpia recursos
   */
  destroy() {
    this.stopCountdown();
    this.element.style.display = 'none';
    this.fireEvent('countdown:destroyed');
  }
}

/**
 * Administrador global de contadores
 */
class CountdownManager {
  constructor() {
    this.countdowns = new Map();
    this.init();
  }

  init() {
    // Inicializar todos los contadores existentes
    this.initializeCountdowns();
    
    // Observar cambios en el DOM para nuevos contadores
    if (typeof MutationObserver !== 'undefined') {
      this.observeDOM();
    }
  }

  initializeCountdowns() {
    const countdownElements = document.querySelectorAll('.programmable-countdown');
    
    countdownElements.forEach(element => {
      if (!this.countdowns.has(element.id)) {
        const countdown = new ProgrammableCountdown(element);
        this.countdowns.set(element.id, countdown);
      }
    });
  }

  observeDOM() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.classList && node.classList.contains('programmable-countdown')) {
              this.initializeCountdown(node);
            }
            
            // Buscar dentro del nodo añadido
            const countdowns = node.querySelectorAll && node.querySelectorAll('.programmable-countdown');
            if (countdowns) {
              countdowns.forEach(countdown => this.initializeCountdown(countdown));
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  initializeCountdown(element) {
    if (!this.countdowns.has(element.id)) {
      const countdown = new ProgrammableCountdown(element);
      this.countdowns.set(element.id, countdown);
    }
  }

  getCountdown(id) {
    return this.countdowns.get(id);
  }

  destroyCountdown(id) {
    const countdown = this.countdowns.get(id);
    if (countdown) {
      countdown.destroy();
      this.countdowns.delete(id);
    }
  }

  destroyAll() {
    this.countdowns.forEach((countdown, id) => {
      countdown.destroy();
    });
    this.countdowns.clear();
  }
}

// Inicializar el administrador cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.CountdownManager = new CountdownManager();
  });
} else {
  window.CountdownManager = new CountdownManager();
}

// API global para facilitar el uso
window.ProgrammableCountdown = {
  create: (elementId, config = {}) => {
    const element = document.getElementById(elementId);
    if (element && window.CountdownManager) {
      return window.CountdownManager.initializeCountdown(element);
    }
  },
  
  get: (id) => {
    return window.CountdownManager ? window.CountdownManager.getCountdown(id) : null;
  },
  
  destroy: (id) => {
    if (window.CountdownManager) {
      window.CountdownManager.destroyCountdown(id);
    }
  }
};